import React from "react";
import styled from "styled-components";
import Tag from "./Tag";
import { OUTLET_LABEL, contractTypeName, isChannelOutlet, shopTypeName } from "src/constants/storeAxis";

/**
 * 매장 한 곳의 분류를 계약형태와 매장형태 두 값으로 함께 보여 준다 (VMD-403).
 *
 * 종전에는 `store_type_name` 한 값만 보여 줬고, 그 값이 네 축을 하나로 접은
 * 파생이라 서로 다른 분류가 서로를 가렸다 — 백화점아울렛 122곳 중 아울렛으로
 * 보이는 것이 43곳뿐이고 나머지는 상설에 흡수됐다.
 *
 * **두 컬럼이 아직 비어 있을 수 있다.** 값을 채우는 기록자는 19:00 포털 크론과
 * 로그인 경로 둘이고, 운영 DDL 이 적용된 직후에는 둘 중 어느 쪽도 아직 돌지
 * 않아 전 매장이 null 이다. 그때 빈 칸을 보여 주면 목록이 쓸모없어지므로
 * 기존 `store_type_name` 으로 되돌아간다. 크론이 한 번 돌면 자연히 두 축이 뜬다.
 */
interface StoreAxisTagsProps {
  contractTypeCd?: string | null;
  analyticStoreType?: string | null;
  isPermanent?: boolean | null;
  /** 유통채널 07 직영아울렛 판정에 쓴다. 앱에서는 `OL` 로 보인다. */
  storeType?: string | null;
  /** 두 축이 아직 비어 있을 때 보여 줄 기존 매장 구분 라벨 */
  fallbackName?: string | null;
}

const StoreAxisTags = (props: StoreAxisTagsProps) => {
  const contract = contractTypeName(props.contractTypeCd);
  const shop = shopTypeName(props.analyticStoreType);

  // 유통채널 07 인데 매장형태가 L 이 아닌 매장은 매장형태 태그만 보면 아울렛인
  // 것을 알 수 없다. 운영에 X30046 LF스퀘어광양(직) 한 곳이고 매장형태가 A
  // 백화점이다. 필터에서는 백화점아울렛으로 걸리므로(백엔드가 L 또는 07 로 묶는다)
  // 표시에서 빠지면 '아울렛으로 걸렀는데 태그는 백화점' 이 된다.
  //
  // 매장형태 라벨을 백화점아울렛으로 덮어쓰지는 않는다 — 등록된 매장형태는 실제로
  // A 백화점이고, 아울렛인 근거는 유통채널이라는 다른 축이다. 그래서 상설과 같은
  // 모양으로 별도 태그를 하나 더 단다.
  const channelOutlet = isChannelOutlet({
    analytic_store_type: props.analyticStoreType,
    store_type: props.storeType,
  });

  if (!contract && !shop) {
    // 두 축이 다 빈 분기다. 운영 실측(2026-10-26) — ST 브랜드 아울렛 세 곳
    // (ST30005, ST30007, ST30009)이 상시 여기 들어온다. 크론 브랜드 화이트리스트가
    // X I M V A 라 ST 는 영구 제외이고, 그 매장들은 두 축이 영영 비어 있어
    // 로그인으로만 채워진다. 전환 기간 한정이 아니다.
    //
    // 아울렛 태그는 fallbackName 을 보고 정한다. 그 라벨이 이미 아울렛을 말하고
    // 있으면(ST 세 곳이 그렇다) 태그를 더하면 두 번 보인다. 반대로 그 라벨은
    // 백엔드 common_code 조인 결과라 프런트가 보장하지 못하므로, OL 인데 라벨이
    // 아울렛이 아닌 데이터가 생기면 태그가 다시 필요하다 — 불변식을 가정하지 않고
    // 실제 값을 비교하는 이유다. fallbackName 이 아예 없을 때도 태그가 뜬다.
    const outletTagNeeded = channelOutlet && props.fallbackName !== OUTLET_LABEL;

    if (!props.isPermanent && !outletTagNeeded) {
      return <Tag value={props.fallbackName ?? ""} color="level1" />;
    }

    return (
      <AxisTagSet>
        {props.fallbackName ? <Tag value={props.fallbackName} color="level1" /> : null}
        {outletTagNeeded ? <Tag value={OUTLET_LABEL} color="level2" /> : null}
        {props.isPermanent ? <Tag value="상설" /> : null}
      </AxisTagSet>
    );
  }

  return (
    <AxisTagSet>
      {contract ? <Tag value={contract} color="level1" /> : null}
      {shop ? <Tag value={shop} color="level2" /> : null}
      {channelOutlet ? <Tag value={OUTLET_LABEL} color="level2" /> : null}
      {props.isPermanent ? <Tag value="상설" /> : null}
    </AxisTagSet>
  );
};

const AxisTagSet = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
`;

export default StoreAxisTags;
